// useMe() pour le frontend Solid.
//
// Stratégie hybride pendant la migration BetterAuth :
// 1. Tente d'abord la session BetterAuth via authClient.useSession()
// 2. Si null (BetterAuth pas activé OU session BetterAuth absente),
//    fallback sur /auth/me legacy via TanStack Query
//
// Cela garde l'API .data?.roles?.includes("admin") fonctionnelle pour
// tous les opérateurs, qu'ils soient sur l'ancien auth ou le nouveau.

import { createQuery } from "@tanstack/solid-query";
import { authClient } from "./auth-client";
import { apiFetch } from "./api-fetch";

export interface MeData {
  id?: string;
  email?: string | null;
  name?: string;
  roles?: string[];
  disabledAt?: string | Date | null;
  lastLoginAt?: string | Date | null;
  mustChangePassword?: boolean;
  [k: string]: unknown;
}

export interface UseMeResult {
  data: MeData | null;
  isLoading: boolean;
  isError: boolean;
}

/**
 * Renvoie un objet réactif { data, isLoading, isError }, hybride :
 * - BetterAuth session si disponible (préféré)
 * - Sinon legacy /auth/me (via TanStack Query, cache 5 min)
 */
export function useMe(): UseMeResult {
  // Session BetterAuth (réactif Solid)
  let baSession: ReturnType<typeof authClient.useSession> | null = null;
  try {
    baSession = authClient.useSession();
  } catch (_err) {
    // authClient peut throw si Provider absent ; on tombe en fallback legacy
    baSession = null;
  }

  // Legacy /auth/me fallback (TanStack Query)
  const legacyQuery = createQuery(() => ({
    queryKey: ["auth-me-legacy"],
    queryFn: async (): Promise<MeData | null> => {
      try {
        const r = await apiFetch("/auth/me", { credentials: "include" });
        if (!r.ok) return null;
        const j = (await r.json()) as MeData & { user?: MeData };
        // Le contrat /auth/me renvoie soit { user } legacy soit { id, name, ... }
        return j.id ? j : j.user || null;
      } catch (_err) {
        return null;
      }
    },
    staleTime: 5 * 60 * 1000,
  }));

  return new Proxy({} as UseMeResult, {
    get(_target, prop) {
      // BetterAuth d'abord
      if (baSession) {
        const s = baSession();
        const baUser = s?.data?.user as MeData | null | undefined;
        if (baUser?.id) {
          if (prop === "data") return baUser;
          if (prop === "isLoading") return s?.isPending === true;
          if (prop === "isError") return Boolean(s?.error);
          return undefined;
        }
      }
      // Fallback legacy
      if (prop === "data") return legacyQuery.data || null;
      if (prop === "isLoading") return legacyQuery.isLoading;
      if (prop === "isError") return legacyQuery.isError;
      return undefined;
    },
  });
}
